// PipelineResult.jsx
import React, { useState } from "react";
import { Card, Button, Table, Typography, message, Tag, Divider } from "antd";
import { ArrowLeftOutlined, FileExcelOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { Tabs } from "antd";
// import DynamicTable from "../Components/DynamicTable";
import PipelineChart from "./PipelineChart";

const { Text } = Typography;

export default function PipelineResult({ result, fields, objectName, onClose }) {
  const [selectedFields, setSelectedFields] = useState([]);
  const [activeTab, setActiveTab] = useState("table");
  const isChartSupported =
  ["grouped", "mergedJoin"].includes(result?.resultType);


  if (!result) return null;

  // -------------------------------
  // Determine effective fields
  // -------------------------------
  let effectiveFields = result.fields || fields || [];
  let childTableFields = result.childTableFields || fields || [];

  const hasExpandableRows =
    Array.isArray(result?.results) &&
    result.results.some((r) => Array.isArray(r.records) && r.records.length > 0);

  // ---------- Grouped / Intersection ----------
  if (["grouped", "intersection"].includes(result.resultType)) {
    const aliases = (result.grouped_fields?.aggregate_aliases || []).filter(
      (name) => name !== "records"
    );

    const metaMap = Object.fromEntries(effectiveFields.map((f) => [f.name, f]));

    effectiveFields = aliases.map((name) => ({
      _id: name,
      name,
      label: metaMap[name]?.label || name,
      type: metaMap[name]?.type || "text",
      is_aggregate: true,
      width: 180,
    }));
  }
  // ---------- Child Join ----------
  else if (result.resultType === "childJoin") {
    const effectiveChildFields = result.childTableFields || fields || [];
    const metaMap = Object.fromEntries(effectiveChildFields.map((f) => [f.name, f]));

    childTableFields = (result.childTableFields || [])
      .filter((f) => typeof f?.name === "string" && f.name.includes("."))
      .map((f) => {
        const [, childFieldName] = f.name.split(".");
        const meta = metaMap[f.name];
        return {
          _id: childFieldName,
          name: childFieldName,
          label: meta?.label || childFieldName,
          type: meta?.type || "text",
          is_child_field: true,
          width: 180,
        };
      });
  }
  // ---------- Merged Join ----------
  else if (result.resultType === "mergedJoin") {
    if (result.grouped_fields?.aggregate_aliases?.length > 0) {
      effectiveFields = result.grouped_fields.aggregate_aliases
        .filter((name) => name !== "records")
        .map((name) => ({ name, label: name, type: "text" }));
    } else {
      const allKeys = new Set();
      result.results.forEach((rec) => {
        Object.keys(rec).forEach((k) => {
          if (["_id", "records"].includes(k)) return;
          const val = rec[k];
          if (val && typeof val === "object" && !Array.isArray(val)) {
            Object.keys(val).forEach((nestedKey) => allKeys.add(`${k}.${nestedKey}`));
          } else {
            allKeys.add(k);
          }
        });
      });
      effectiveFields = Array.from(allKeys).map((key) => ({
        name: key,
        label: key,
        type: "text",
      }));
    }
  }

  // -------------------------------
  // Render cell values
  // -------------------------------
  const renderValue = (value, fieldMeta) => {
    if (value === null || value === undefined) return "";

    const type = fieldMeta?.type;

    // Lookup / Reference objects
    if (value && typeof value === "object" && ("_id" in value || "Name" in value)) {
      const displayName = value.Name || value.label || value.name || "Unknown";
      const id = value._id || value.id;
      const parent_object_name = fieldMeta?.parent_object_name || "";
      return id ? (
        <a href={`/record/${parent_object_name}/${id}`} target="_blank" rel="noopener noreferrer">
          {displayName}
        </a>
      ) : displayName;
    }

    // Type-based formatting
    if (type === "date") return value ? new Date(value).toLocaleDateString() : "";
    if (type === "datetime") return value ? new Date(value).toLocaleString() : "";
    if (type === "boolean") return value ? "✔️" : "❌";
    if (type === "number" || type === "currency") return value.toLocaleString();

    // Fallback: render object as JSON
    if (typeof value === "object") {
      return (
        <pre style={{ background: "#f5f5f5", padding: 10, borderRadius: 6, maxWidth: 600, overflowX: "auto" }}>
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }

    return value.toString();
  };

  // -------------------------------
  // Helpers
  // -------------------------------
  const flattenObject = (obj, prefix = "", res = {}) => {
    Object.entries(obj || {}).forEach(([k, v]) => {
      const newKey = prefix ? `${prefix}.${k}` : k;
  
      // Check if it's a nested object but not an array or a lookup
      if (v && typeof v === "object" && !Array.isArray(v) && !("_id" in v && "Name" in v)) {
        flattenObject(v, newKey, res);
      } else {
        // Find metadata for this path in childTableFields
        const fieldMeta = childTableFields.find((f) => f.name === newKey);
  
        // Render value using renderValue
        res[newKey] = renderValue(v, fieldMeta);
      }
    });
    return res;
  };
  

  const getNestedValue = (record, path) => path.split(".").reduce((acc, k) => (acc ? acc[k] : undefined), record);

  const exportToExcel = () => {
    if (!result?.results) return message.error("No result to export");

    try {
      const exportData = result.results.map((row) => {
        const flat = {};
        Object.keys(row).forEach((key) => {
          const value = row[key];
          if (value && typeof value === "object" && value.Name) flat[key === "_id" ? "Name" : `${key}.Name`] = value.Name;
          else flat[key] = value;
        });
        return flat;
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Results");
      XLSX.writeFile(wb, `Pipeline_Result_${objectName}.xlsx`);
      message.success("Excel exported");
    } catch (e) {
      console.error(e);
      message.error("Export failed");
    }
  };

  // -------------------------------
  // Render metadata info
  // -------------------------------
  const renderMetaInfo = () => (
    <div style={{ marginBottom: 12 }}>
      <div><strong>Result Type: </strong><Tag color="blue">{result.resultType || "records"}</Tag></div>

      {result.grouped_fields?.group_keys?.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <strong>Group Levels: </strong>
          {result.grouped_fields.group_keys.map((g) => <Tag key={g} color="purple">{g}</Tag>)}
        </div>
      )}

      {result.facet_fields?.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <strong>Facet Buckets: </strong>
          {result.facet_fields.map((f) => <Tag key={f} color="green">{f}</Tag>)}
        </div>
      )}

      <Divider />
    </div>
  );

  // -------------------------------
  // Render
  // -------------------------------
  return (
    <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "#fff", zIndex: 9999, padding: 20, overflow: "auto" }}>
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <Button type="primary" icon={<FileExcelOutlined />} onClick={exportToExcel}>Export to Excel</Button>
        <Button type="default" icon={<ArrowLeftOutlined />} onClick={onClose}>Back</Button>
      </div>

      <Card title="Pipeline Result" size="small" style={{ width: "100%", minHeight: "90vh" }}>
        {renderMetaInfo()}
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane tab="Table View" key="table">
            {
              result.results?.length > 0 ? 
              (
                <Table
                  bordered
                  size="middle"
                  scroll={{ x: "100%", y: "72vh" }}
                  pagination={false}
                  rowKey={(row) => row._id || Math.random()}
                  columns={effectiveFields
                    .filter((f) => !selectedFields.length || selectedFields.includes(f.name))
                    .map((f) => ({
                      title: f.label || f.name,
                      dataIndex: f.name,
                      key: f.name,
                      width: 200,
                      render: (_, record) => renderValue(getNestedValue(record, f.name), f),
                    }))
                  }
                  dataSource={
                    selectedFields.length
                      ? result.results.map((rec) => {
                          const filtered = {};
                          selectedFields.forEach((f) => filtered[f] = rec[f]);
                          filtered.records = rec.records;
                          filtered._id = rec._id;
                          return filtered;
                        })
                      : result.results
                  }
                  expandable={{
                    expandRowByClick: true,
                    columnWidth: 50,
                    rowExpandable: (record) => Array.isArray(record.records) && record.records.length > 0,
                    expandedRowRender: (record) => {
                      const grouped = record.records || [];
                      if (!grouped.length) return <div style={{ padding: 12 }}>No grouped records found</div>;

                      const innerColumns = Object.keys(flattenObject(grouped[0]))
                        .filter((key) => !["_id", "records"].includes(key) && !key.startsWith("_id.") && !key.endsWith("_id"))
                        .map((key, index) => {
                          const fieldMeta = childTableFields.find((f) => f.name === key);
                          return {
                            title: key,
                            dataIndex: key,
                            key: `col_${index}_${key.replace(/\./g, "_")}`,
                            width: 180,
                            render: (_, row) => row[key], // already rendered inside flattenObject
                          };
                        });

                      return (
                        <Table
                          columns={innerColumns}
                          dataSource={grouped.map((r) => flattenObject(r))}
                          pagination={false}
                          size="small"
                          rowKey={(row) => row["_id"] || Math.random()}
                        />
                      );
                    },
                  }}
                />
              ) 
              : 
              (<Text type="secondary">No records returned</Text>)
            }
          </Tabs.TabPane>
          {isChartSupported && (
            <Tabs.TabPane tab="Chart View" key="chart">
              <PipelineChart result={result} />
            </Tabs.TabPane>
          )}
        </Tabs>   
      </Card>
    </div>
  );
}